import React, { useState, useEffect, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import Header from "./components/Header";
import Hero from "./components/Hero";
import CarGallery from "./components/CarGallery";
import Services from "./components/Services";
import Testimonials from "./components/Testimonials";
import About from "./components/About";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import AdminLogin from "./components/AdminLogin";
import AdminDashboard from "./components/AdminDashboard";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AccessibilityStatement from "./components/AccessibilityStatement";
import ConsumerRights from "./components/ConsumerRights";
import AccessibilityMenu from "./components/AccessibilityMenu";
import InstallPrompt from "./components/InstallPrompt";
import { Toaster } from "./components/ui/sonner";

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-black flex items-center justify-center" dir="rtl">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-amber-500 mx-auto mb-4"></div>
      <p className="text-white text-lg">טוען...</p>
    </div>
  </div>
);

const Home = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Small delay to ensure all components mount properly
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (!isReady) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-black text-white">
        <Header />
        <Hero />
        <CarGallery />
        <Services />
        <Testimonials />
        <About />
        <Contact />
        <Footer />
        <AccessibilityMenu />
        <InstallPrompt />
        <Toaster />
      </div>
    </ErrorBoundary>
  );
};

const Admin = () => {
  const [auth, setAuth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if admin is already logged in
    try {
      const savedAuth = localStorage.getItem('admin_auth');
      if (savedAuth) {
        setAuth(savedAuth);
      }
    } catch (error) {
      console.error('Error reading auth:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = (authToken) => {
    setAuth(authToken);
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('admin_auth');
    } catch (error) {
      console.error('Error removing auth:', error);
    }
    setAuth(null);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!auth) {
    return (
      <ErrorBoundary>
        <AdminLogin onLogin={handleLogin} />
        <AccessibilityMenu />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AdminDashboard auth={auth} onLogout={handleLogout} />
      <AccessibilityMenu />
    </ErrorBoundary>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <div className="App mobile-container overflow-x-hidden w-full">
        <BrowserRouter>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/terms" element={<ErrorBoundary><TermsOfService /><AccessibilityMenu /></ErrorBoundary>} />
              <Route path="/privacy" element={<ErrorBoundary><PrivacyPolicy /><AccessibilityMenu /></ErrorBoundary>} />
              <Route path="/accessibility" element={<ErrorBoundary><AccessibilityStatement /><AccessibilityMenu /></ErrorBoundary>} />
              <Route path="/consumer-rights" element={<ErrorBoundary><ConsumerRights /><AccessibilityMenu /></ErrorBoundary>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
